import NavItem from "./NavItem";
import MenuLinks from "./config";
import PropTypes from "prop-types";
import Logo from "src/components/Logo";
import useAuth from "src/hooks/useAuth";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import MyAvatar from "src/components/MyAvatar";
import Scrollbars from "src/components/Scrollbars";
import { Link as RouterLink, useLocation, matchPath } from "react-router-dom";
import { alpha, makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Link,
  List,
  Drawer,
  Typography,
  ListSubheader,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import GetAppIcon from "@mui/icons-material/GetApp";
import { PATH_APP } from "src/routes/paths";
import IsRole from "src/utils/IsRole";
import Roles from "src/utils/Roles";
import Label from "src/components/Label";
import { WebVersion } from "src/config";
import { BUILD_ID, formatBuildId, IS_STAMPED_BUILD } from "src/utils/buildInfo";
import useEmployeeRole from "src/hooks/useEmployeeRole";
import { useClaims } from "src/hooks/useClaims";
import { usePWAInstall } from "src/contexts/PWAInstallContext";
import useSocialAccess from "src/views/app/Admin/social/useSocialAccess";
import useStockAlerts from "src/hooks/useStockAlerts";
import ROLES_ENUM from "src/utils/enums";
import { INVENTARIO_ADDON_NAME } from "src/utils/gestion/inventarioHub";

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.mode === "light";

  return {
    drawer: {
      [theme.breakpoints.up("lg")]: {
        flexShrink: 0,
      },
    },
    drawerPaper: {
      width: DRAWER_WIDTH,
      background: theme.palette.background.default,
    },
    subHeader: {
      ...theme.typography.overline,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(5),
      color: theme.palette.text.primary,
    },
    account: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(2, 2.5),
      margin: theme.spacing(1, 2.5, 5),
      borderRadius: theme.shape.borderRadiusSm,
      background: theme.palette.grey[isLight ? 200 : 800],
    },
    doc: {
      padding: theme.spacing(2.5),
      borderRadius: theme.shape.borderRadiusMd,
      backgroundColor: isLight
        ? alpha(theme.palette.primary.main, 0.08)
        : theme.palette.primary.lighter,
    },
    cardPricing: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing(3),
      [theme.breakpoints.up("md")]: {
        padding: theme.spacing(5),
        boxShadow: theme.shadows[25].z8,
        borderRadius: theme.shape.borderRadiusMd,
      },
    },
  };
});

// ----------------------------------------------------------------------

// 2026-08-25 · Chequeo unificado de visibilidad de un item del nav.
// Reglas:
//   • Items no-admin (operativos del empleado) → siempre visibles.
//   • Admin dueño operando (UserEmployeeId=0) → todo visible.
//   • Admin fichado como "working" (owner en el kiosko) → todo visible.
//   • Resto → depende de `condition` (truthy → visible).
// SIN dependencia del switch legacy `user.requestUserIdentity`.
function passesNavCondition(i, employees) {
  if (!i.isAdmin) return true;
  if (employees?.find((e) => e.isAdmin && e.working)) return true;
  if (Number(window.localStorage.getItem("UserEmployeeId")) === 0) return true;
  return Boolean(i.condition);
}

// Un item padre con children (`items[]`) es visible si su propio
// condition pasa O si algún child pasa. Sin esta regla OR, un rol
// que solo tiene permiso para editar categorías perdía la sección
// "Inventario" entera porque su condition padre pedía el permiso
// amplio INVENTARY_MANAGER que no tenía.
function isNavItemVisible(i, employees, currentRole) {
  if (passesNavCondition(i, employees)) return true;
  if (i.items && i.items.length > 0) {
    return i.items.some(
      (c) => IsRole(c.roles, currentRole) && passesNavCondition(c, employees),
    );
  }
  return false;
}

function reduceChild({
  user,
  userState,
  employees,
  array,
  item,
  pathname,
  level,
  role,
}) {
  const key = item.href + level;

  const items = item?.items
    ?.filter((i) => IsRole(i.roles, userState.claims.role))
    ?.filter((i) => passesNavCondition(i, employees));

  if (item.items && items.length > 0) {
    const match = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    array = [
      ...array,
      <NavItem
        key={key}
        level={level}
        icon={item.icon}
        info={item.info}
        href={item.href}
        title={item.title}
        open={Boolean(match)}
      >
        {renderNavItems({
          user,
          userState,
          employees,
          pathname,
          level: level + 1,
          items: items,
          role: role,
        })}
      </NavItem>,
    ];
  } else {
    array = [
      ...array,
      <NavItem
        key={key}
        level={level}
        href={item.href}
        icon={item.icon}
        info={item.info}
        title={item.title}
      />,
    ];
  }

  return array;
}

function renderNavItems({
  user,
  userState,
  employees,
  items,
  pathname,
  level = 0,
  role,
}) {
  return (
    <List disablePadding>
      {items.reduce(
        (array, item) =>
          reduceChild({
            user,
            userState,
            employees,
            array,
            item,
            pathname,
            level,
            role,
          }),
        []
      )}
    </List>
  );
}

NavBar.propTypes = {
  onCloseNav: PropTypes.func,
  isOpenNav: PropTypes.bool,
};

function NavBar({ isOpenNav, onCloseNav }) {
  const { permissions } = useEmployeeRole();
  const { isEmployeeWebSession } = useClaims();

  const classes = useStyles();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const userState = useSelector((state) => state.authJwt);
  const { canInstall, isIOS, triggerInstall } = usePWAInstall();
  const [openIOSDialog, setOpenIOSDialog] = useState(false);
  const { displays } = useSelector((state) => state.displays);
  // Fix6 2026-07-22 · códigos de integraciones activas para el nav.
  // Alimenta la condición del item "Reseñas · PRO" (solo aparece si
  // Google está conectado) y futuros items dependientes.
  const activeIntegrationCodes = useSelector((state) => state.integrations?.activeCodes || []);
  const activeAddOnNames = useSelector((state) => state.addOns?.activeAddOnNames || []);

  const { employees } = useSelector((state) => state.employees);
  // 2026-10-24 · Admin › Redes sociales: solo si el API lo permite (allowlist).
  const { allowed: socialAllowed } = useSocialAccess(
    IsRole(userState.claims.role, Roles.GLOBAL_ADMIN),
    user?.email,
  );

  // 2026-10 · Alertas de stock en tiempo real (solo con add-on y permiso).
  const stockAlertas = useStockAlerts(
    activeAddOnNames.includes(INVENTARIO_ADDON_NAME)
      && (permissions || []).includes(ROLES_ENUM.INVENTARY_GESTION_MANAGER),
  );

  let auxMenuLinks = MenuLinks(
    user,
    userState.user.isOnlyTpv,
    permissions,
    displays.filter((u) => u.isActive),
    activeIntegrationCodes,
    activeAddOnNames,
    { isEmployeeWebSession, socialAllowed, stockAlertas },
  );

  let itemsNavbar = auxMenuLinks.items
    .filter((i) => IsRole(i.roles, userState.claims.role))
    .filter((i) => isNavItemVisible(i, employees, userState.claims.role));

  let bottomItemsNavbar = auxMenuLinks.bottomItems
    .filter((i) => IsRole(i.roles, userState.claims.role))
    .filter((i) => {
      // Bottom items no suelen anidar, mantenemos el chequeo directo
      // sin OR de hijos.
      if (!i.isAdmin) return true;
      else {
        let employeeWorking = employees.find((e) => e.isAdmin && e.working);

        if (employeeWorking) return true;

        if (Number(window.localStorage.getItem("UserEmployeeId")) === 0)
          return true;

        if (i.condition) return i.condition;

        return false;
      }
    });

  useEffect(() => {
    if (isOpenNav && onCloseNav) {
      onCloseNav();
    }
  }, [pathname]);

  const renderContent = (
    <Scrollbars>
      <Box sx={{ px: 2.5, py: 3 }}>
        <RouterLink
          to={() => {
            if (IsRole(userState.claims.role, Roles.USER_NORMAL))
              return PATH_APP.tuCarta.root;

            if (IsRole(userState.claims.role, Roles.USER_FRANQUICIA))
              return PATH_APP.franquicia.root;

            return PATH_APP.franquiciado.root;
          }}
        >
          <Logo />
        </RouterLink>
      </Box>
      {employees?.find(
        (e) => (e?.working && e?.isAdmin) || !user.requestUserIdentity
      ) && (
        <Link underline="none" component={RouterLink} to={PATH_APP.user.root}>
          <div className={classes.account}>
            <MyAvatar />
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
                {user.firstName}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {user.role}
              </Typography>
            </Box>
          </div>
        </Link>
      )}

      <List
        disablePadding
        key={auxMenuLinks.subheader}
        subheader={
          <ListSubheader
            disableSticky
            disableGutters
            className={classes.subHeader}
          >
            {auxMenuLinks.subheader}
          </ListSubheader>
        }
      >
        {renderNavItems({
          user,
          userState,
          employees,
          items: itemsNavbar,
          pathname: pathname,
          role: userState.claims.role,
        })}
      </List>
      <List
        disablePadding
        key={auxMenuLinks.subheader + "bottom"}
        sx={{ position: "fixed", bottom: 0 }}
      >
        {renderNavItems({
          user,
          userState,
          employees,
          items: bottomItemsNavbar,
          pathname: pathname,
          role: userState.claims.role,
        })}
      </List>
    </Scrollbars>
  );

  return (
    <nav className={classes.drawer}>
      <Drawer
        anchor="left"
        open={isOpenNav}
        variant="temporary"
        onClose={onCloseNav}
        classes={{ paper: classes.drawerPaper }}
      >
        {renderContent}

        {canInstall && (
          <Box sx={{ px: 2, py: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GetAppIcon />}
              onClick={() => {
                if (isIOS) setOpenIOSDialog(true);
                else triggerInstall();
              }}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                borderStyle: "dashed",
              }}
            >
              Instalar App
            </Button>
          </Box>
        )}

        <Label
          color="success"
          sx={{ fontSize: "16px", minHeight: "40px", borderRadius: 0 }}
        >
          <Box sx={{ textAlign: "center", py: 0.5 }}>
            <Typography variant="h6" data-testid="app-version">
              {`v${WebVersion}`}
            </Typography>
            {/* Build (commit · fecha): cambia en cada despliegue sin tocar
                nada y es el mismo id que se ve en Admin › Versiones. */}
            <Typography variant="caption" data-testid="app-build" sx={{ display: "block", opacity: 0.8 }}>
              {IS_STAMPED_BUILD ? formatBuildId(BUILD_ID).split(" · ").slice(1).join(" · ") : "dev"}
            </Typography>
          </Box>
        </Label>
      </Drawer>

      {/* ── iOS install instructions dialog ── */}
      <Dialog open={openIOSDialog} onClose={() => setOpenIOSDialog(false)}>
        <DialogTitle>Instalar en iPhone / iPad</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Safari no permite la instalación automática. Sigue estos pasos:
          </Typography>
          <Box component="ol" sx={{ pl: 2, mt: 1 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Pulsa el botón <strong>Compartir</strong> (
              <Box component="span" sx={{ fontSize: 16 }}>⬆</Box>
              ) en la barra inferior de Safari.
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Desplázate y selecciona{" "}
              <strong>«Añadir a pantalla de inicio»</strong>.
            </Typography>
            <Typography component="li" variant="body2">
              Pulsa <strong>Añadir</strong> en la esquina superior derecha.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenIOSDialog(false)} color="primary">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </nav>
  );
}

export default NavBar;
